import { dedupeTeacherSessions } from "./teacherSessionDedupe";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function lessonMinutes(lesson) {
  const minutes = Number(lesson?.duration_minutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 60;
}

// Start times of a lesson that fall inside [start, end). Weekly recurring
// lessons are stored once and repeat every week from their first date.
function occurrencesInRange(lesson, start, end) {
  const anchor = new Date(lesson?.scheduled_at).getTime();
  if (!Number.isFinite(anchor)) return [];
  const from = start.getTime();
  const to = end.getTime();

  if (!lesson.is_recurring_weekly) {
    return anchor >= from && anchor < to ? [anchor] : [];
  }

  const result = [];
  let first = anchor;
  if (first < from) {
    first += Math.ceil((from - first) / WEEK_MS) * WEEK_MS;
  }
  for (let ts = first; ts < to; ts += WEEK_MS) {
    result.push(ts);
  }
  return result;
}

/**
 * Hours per teacher for a period. Group sessions (one lesson row per student)
 * count once. Cancelled lessons are ignored. "done" covers sessions that have
 * already ended, "planned" the ones still to come.
 *
 * Returns a Map: teacherId -> { sessions, doneMinutes, plannedMinutes }.
 */
export function computeTeacherHours(lessons, { start, end, now = new Date() }) {
  const totals = new Map();
  const nowTs = now.getTime();

  dedupeTeacherSessions(lessons || []).forEach((lesson) => {
    const teacherId = lesson?.teacher?.id ?? lesson?.teacher_id;
    if (!teacherId || lesson.status === "cancelled") return;

    const minutes = lessonMinutes(lesson);
    const entry = totals.get(teacherId) || {
      sessions: 0,
      doneMinutes: 0,
      plannedMinutes: 0,
    };

    occurrencesInRange(lesson, start, end).forEach((ts) => {
      entry.sessions += 1;
      if (ts + minutes * 60 * 1000 <= nowTs) {
        entry.doneMinutes += minutes;
      } else {
        entry.plannedMinutes += minutes;
      }
    });

    if (entry.sessions) totals.set(teacherId, entry);
  });

  return totals;
}

export function formatHours(minutes, locale) {
  const hours = (Number(minutes) || 0) / 60;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(hours);
}
